<template>
  <div class="all-devices-management">
    <!-- Admin Access Check -->
    <div v-if="!isUserAdmin" class="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
      <font-awesome-icon icon="shield-alt" class="text-4xl text-red-500 mb-4" />
      <h2 class="text-xl font-bold text-red-800 mb-2">Access Denied</h2>
      <p class="text-red-600">This feature is only available for administrators.</p>
      <el-button type="primary" @click="$router.push('/dashboard')" class="mt-4">
        <font-awesome-icon icon="arrow-left" class="mr-1" />
        Back to Dashboard
      </el-button>
    </div>

    <!-- Admin Content -->
    <div v-else class="bg-white rounded-lg shadow-md p-6">
      <div class="flex justify-between items-center mb-6">
        <h1 class="text-2xl font-bold text-gray-800">
          <font-awesome-icon icon="mobile-alt" class="mr-2" />
          All Registered Users & Devices
        </h1>
        <div class="flex gap-3">
          <el-button
            type="info"
            size="small"
            @click="showFilters = !showFilters"
          >
            <font-awesome-icon :icon="showFilters ? 'eye-slash' : 'filter'" class="mr-1" />
            {{ showFilters ? 'Hide' : 'Show' }} Filters
          </el-button>
          <el-button
            type="success"
            size="small"
            @click="exportDevices"
            :disabled="loading"
          >
            <font-awesome-icon icon="file-excel" class="mr-1" />
            Export Excel
          </el-button>
          <el-button
            type="warning"
            size="small"
            @click="refreshDevices"
            :loading="loading"
          >
            <font-awesome-icon icon="sync-alt" class="mr-1" />
            Refresh
          </el-button>
        </div>
      </div>

      <!-- Statistics Cards -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div class="bg-blue-50 p-4 rounded-lg">
          <div class="flex items-center">
            <font-awesome-icon icon="users" class="text-2xl text-blue-500 mr-3" />
            <div>
              <p class="text-sm text-gray-600">Total Users</p>
              <p class="text-xl font-bold text-blue-600">{{ totalUsers }}</p>
            </div>
          </div>
        </div>
        <div class="bg-green-50 p-4 rounded-lg">
          <div class="flex items-center">
            <font-awesome-icon icon="mobile-alt" class="text-2xl text-green-500 mr-3" />
            <div>
              <p class="text-sm text-gray-600">Total Devices</p>
              <p class="text-xl font-bold text-green-600">{{ totalDevices }}</p>
            </div>
          </div>
        </div>
        <div class="bg-yellow-50 p-4 rounded-lg">
          <div class="flex items-center">
            <font-awesome-icon icon="check-circle" class="text-2xl text-yellow-500 mr-3" />
            <div>
              <p class="text-sm text-gray-600">Active Devices</p>
              <p class="text-xl font-bold text-yellow-600">{{ activeDevices }}</p>
            </div>
          </div>
        </div>
        <div class="bg-red-50 p-4 rounded-lg">
          <div class="flex items-center">
            <font-awesome-icon icon="times-circle" class="text-2xl text-red-500 mr-3" />
            <div>
              <p class="text-sm text-gray-600">Inactive Devices</p>
              <p class="text-xl font-bold text-red-600">{{ inactiveDevices }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Filters Section -->
      <div v-if="showFilters" class="mb-6 p-4 bg-gray-50 rounded-lg">
        <h3 class="text-lg font-semibold text-gray-700 mb-4">Filter Options</h3>
        <el-form :model="filters" inline class="filter-form">
          <el-form-item label="Username">
            <el-input
              v-model="filters.username"
              placeholder="Enter username"
              clearable
              class="w-48"
            />
          </el-form-item>
          
          <el-form-item label="Device Type">
            <el-select v-model="filters.deviceType" placeholder="Select device type" clearable class="w-48">
              <el-option label="Android" value="android" />
              <el-option label="iOS" value="ios" />
              <el-option label="Web" value="web" />
            </el-select>
          </el-form-item>

          <el-form-item label="Status">
            <el-select v-model="filters.isActive" placeholder="Select status" clearable class="w-48">
              <el-option label="Active" :value="true" />
              <el-option label="Inactive" :value="false" />
            </el-select>
          </el-form-item>

          <el-form-item label="Date Range">
            <el-date-picker
              v-model="dateRange"
              type="datetimerange"
              range-separator="to"
              start-placeholder="Start date"
              end-placeholder="End date"
              format="dd/MM/yyyy"
              value-format="yyyy-MM-dd"
              class="w-80"
            />
          </el-form-item>

          <el-form-item>
            <el-button type="primary" @click="applyFilters">
              <font-awesome-icon icon="search" class="mr-1" />
              Apply Filters
            </el-button>
            <el-button @click="clearFilters">
              <font-awesome-icon icon="times" class="mr-1" />
              Clear
            </el-button>
          </el-form-item>
        </el-form>
      </div>

      <!-- Devices Table -->
      <div v-if="allDevices.length > 0">
        <el-table
          :data="allDevices"
          stripe
          style="width: 100%"
          v-loading="loading"
        >
          <el-table-column
            prop="username"
            label="Username"
            width="200"
            sortable
          >
            <template slot-scope="scope">
              <div class="flex items-center">
                <font-awesome-icon icon="user" class="mr-2 text-blue-500" />
                {{ scope.row.username }}
              </div>
            </template>
          </el-table-column>

          <el-table-column
            prop="deviceName"
            label="Device Name"
            width="200"
          >
            <template slot-scope="scope">
              <div class="flex flex-col">
                <!-- <font-awesome-icon 
                  :icon="getDeviceIcon(scope.row.deviceType)" 
                  class="mr-2 text-blue-500" 
                /> -->
                <p class="p-0 m-0">{{ scope.row.deviceName }}</p>
                <div>
                  <el-tag 
                    v-if="scope.row.isPrimary" 
                    size="mini" 
                    type="success" 
                  >
                    Primary
                  </el-tag>
                </div>
              </div>
            </template>
          </el-table-column>

          <el-table-column
            prop="deviceType"
            label="Type"
            width="100"
            sortable
          />

          <el-table-column
            prop="osVersion"
            label="OS Version"
            width="120"
          />

          <el-table-column
            prop="appVersion"
            label="App Version"
            width="120"
          />

          <el-table-column
            prop="isActive"
            label="Status"
            width="100"
            sortable
          >
            <template slot-scope="scope">
              <el-tag 
                :type="scope.row.isActive ? 'success' : 'danger'" 
                size="mini"
              >
                {{ scope.row.isActive ? 'Active' : 'Inactive' }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column
            prop="lastUsedAt"
            label="Last Used"
            width="160"
            sortable
          >
            <template slot-scope="scope">
              {{ formatDate(scope.row.lastUsedAt) }}
            </template>
          </el-table-column>

          <el-table-column
            prop="createdAt"
            label="Registered"
            width="160"
            sortable
          >
            <template slot-scope="scope">
              {{ formatDate(scope.row.createdAt) }}
            </template>
          </el-table-column>

          <el-table-column
            label="Actions"
            width="315"
          >
            <template slot-scope="scope">
              <div class="flex space-x-2">
                <el-button
                  type="info"
                  @click="viewDeviceDetail(scope.row)"
                  :disabled="loading"
                  size="mini"
                >
                  <font-awesome-icon icon="eye" />
                  View
                </el-button>
                <el-button
                  v-if="scope.row.isActive"
                  type="warning"
                  @click="deactivateDevice(scope.row)"
                  :disabled="loading"
                  size="mini"
                >
                  <font-awesome-icon icon="ban" />
                  Deactivate
                </el-button>
                <el-button
                  v-else
                  type="success"
                  @click="activateDevice(scope.row)"
                  :disabled="loading"
                  size="mini"
                >
                  <font-awesome-icon icon="check" />
                  Activate
                </el-button>
                <el-button
                  type="danger"
                  @click="removeDevice(scope.row)"
                  :disabled="loading"
                  size="mini"
                >
                  <font-awesome-icon icon="trash" />
                  Remove
                </el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>

        <!-- Pagination -->
        <div class="flex justify-center mt-6">
          <el-pagination
            @size-change="handleSizeChange"
            @current-change="handlePageChange"
            :current-page="currentPage"
            :page-sizes="[10, 20, 50, 100]"
            :page-size="pageSize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="totalElements"
          />
        </div>
      </div>

      <!-- Empty State -->
      <div v-else-if="!loading" class="text-center py-12">
        <font-awesome-icon icon="mobile-alt" class="text-6xl text-gray-300 mb-4" />
        <h3 class="text-xl font-semibold text-gray-600 mb-2">No Devices Found</h3>
        <p class="text-gray-500">No registered devices found in the system.</p>
      </div>
    </div>

    <!-- Device Detail Dialog -->
    <el-dialog
      title="Device Details"
      :visible.sync="showDetailDialog"
      width="60%"
    >
      <div v-if="selectedDevice" class="device-detail">
        <div class="grid grid-cols-2 gap-4">
          <div>
            <h4 class="font-semibold text-gray-700 mb-2">Basic Information</h4>
            <div class="space-y-2 text-sm">
              <p><strong>Username:</strong> {{ selectedDevice.username }}</p>
              <p><strong>Device Name:</strong> {{ selectedDevice.deviceName }}</p>
              <p><strong>Device Type:</strong> {{ selectedDevice.deviceType }}</p>
              <p><strong>Device Token:</strong> {{ selectedDevice.deviceToken ? selectedDevice.deviceToken.substring(0, 20) + '...' : 'N/A' }}</p>
              <p><strong>FCM Token:</strong> {{ selectedDevice.fcmToken ? selectedDevice.fcmToken.substring(0, 20) + '...' : 'N/A' }}</p>
            </div>
          </div>
          <div>
            <h4 class="font-semibold text-gray-700 mb-2">System Information</h4>
            <div class="space-y-2 text-sm">
              <p><strong>OS Version:</strong> {{ selectedDevice.osVersion || 'N/A' }}</p>
              <p><strong>App Version:</strong> {{ selectedDevice.appVersion || 'N/A' }}</p>
              <p><strong>Status:</strong> 
                <el-tag :type="selectedDevice.isActive ? 'success' : 'danger'" size="mini">
                  {{ selectedDevice.isActive ? 'Active' : 'Inactive' }}
                </el-tag>
              </p>
              <p><strong>Is Primary:</strong> 
                <el-tag :type="selectedDevice.isPrimary ? 'success' : 'info'" size="mini">
                  {{ selectedDevice.isPrimary ? 'Yes' : 'No' }}
                </el-tag>
              </p>
            </div>
          </div>
        </div>
        <div class="mt-4">
          <h4 class="font-semibold text-gray-700 mb-2">Timestamps</h4>
          <div class="grid grid-cols-2 gap-4 text-sm">
            <p><strong>Created At:</strong> {{ formatDate(selectedDevice.createdAt) }}</p>
            <p><strong>Last Used At:</strong> {{ formatDate(selectedDevice.lastUsedAt) }}</p>
          </div>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="showDetailDialog = false">Close</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import moment from 'moment'

export default {
  name: 'AllDevicesView',
  data() {
    return {
      showFilters: false,
      showDetailDialog: false,
      selectedDevice: null,
      currentPage: 1,
      pageSize: 10,
      dateRange: null,
      filters: {
        username: '',
        deviceType: '',
        isActive: null,
        startDate: '',
        endDate: ''
      }
    }
  },
  computed: {
    allDevices() {
      return this.$store.getters['device/getDevices'] || []
    },
    loading() {
      return this.$store.getters['device/isLoading']
    },
    error() {
      return this.$store.getters['device/getError']
    },
    message() {
      return this.$store.getters['device/getMessage']
    },
    currentUsername() {
      return this.$store.state.info?.sub || 'Unknown'
    },
    isUserAdmin() {
      // Check if user is admin based on username or roles
      if (this.currentUsername.toLowerCase().includes('admin')) {
        return true;
      }
      // Check if user has admin role in JWT token
      const roles = this.$store.state.userRoles;
      if (roles && Array.isArray(roles)) {
        return roles.some(role => 
          role.authority === 'ROLE_ADMIN' || 
          role.authority === 'ADMIN' ||
          role === 'ROLE_ADMIN' ||
          role === 'ADMIN'
        );
      }
      return false;
    },
    totalUsers() {
      const uniqueUsers = new Set(this.allDevices.map(device => device.username))
      return uniqueUsers.size
    },
    totalDevices() {
      return this.allDevices.length
    },
    activeDevices() {
      return this.allDevices.filter(device => device.isActive).length
    },
    inactiveDevices() {
      return this.allDevices.filter(device => !device.isActive).length
    },
    totalElements() {
      return this.$store.getters['device/getTotalElements']
    },
    pagination() {
      return this.$store.getters['device/getPagination']
    },
  },
  watch: {
    error(newError) {
      if (newError) {
        this.$message.error(newError)
      }
    },
    message(newMessage) {
      if (newMessage) {
        this.$message.success(newMessage)
      }
    },
    dateRange(newRange) {
      if (newRange && newRange.length === 2) {
        this.filters.startDate = newRange[0]
        this.filters.endDate = newRange[1]
      } else {
        this.filters.startDate = ''
        this.filters.endDate = ''
      }
    }
  },
  mounted() {
    if (this.isUserAdmin) {
      this.loadAllDevices()
    }
  },
  methods: {
    async loadAllDevices(page = 0) {
      try {
        const params = {
          page,
          size: this.pageSize,
          filters: this.getActiveFilters()
        }
        
        const response = await this.$store.dispatch('device/getAllDevices', params)
        
        // Update current page based on actual response
        if (response && typeof response.number !== 'undefined') {
          this.currentPage = response.number + 1 // Convert from 0-based to 1-based
        } else {
          this.currentPage = page + 1 // Fallback
        }
        
      } catch (error) {
        console.error('Error loading all devices:', error)
      }
    },

    async refreshDevices() {
      await this.loadAllDevices(this.currentPage - 1) // Maintain current page on refresh
    },

    async applyFilters() {
      this.currentPage = 1 // Reset to first page when applying filters
      await this.loadAllDevices(0)
    },

    clearFilters() {
      this.filters = {
        username: '',
        deviceType: '',
        isActive: null,
        startDate: '',
        endDate: ''
      }
      this.dateRange = null
      this.currentPage = 1 // Reset to first page when clearing filters
      this.loadAllDevices(0)
    },

    getActiveFilters() {
      const activeFilters = {}
      Object.entries(this.filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          activeFilters[key] = value
        }
      })
      return activeFilters
    },

    async exportDevices() {
      try {
        // This would need to be implemented on the backend
        this.$message.info('Export functionality will be implemented soon')
      } catch (error) {
        console.error('Error exporting devices:', error)
      }
    },

    handlePageChange(page) {
      this.currentPage = page
      this.loadAllDevices(page - 1) // Convert from 1-based to 0-based for API
    },

    handleSizeChange(size) {
      this.pageSize = size
      this.currentPage = 1 // Reset to first page when changing page size
      this.loadAllDevices(0)
    },

    viewDeviceDetail(device) {
      this.selectedDevice = device
      this.showDetailDialog = true
    },

    async activateDevice(device) {
      try {
        await this.$confirm('Are you sure you want to activate this device?', 'Confirm Action', {
          type: 'warning'
        })
        
        const deviceData = {
          action: 'ACTIVATE',
          username: device.username,
          fcmToken: device.deviceToken || device.fcmToken || device.idDevice // Support multiple field names
        }

        await this.$store.dispatch('device/manageDevice', deviceData)
        await this.loadAllDevices(this.currentPage - 1) // Maintain current page
        
      } catch (error) {
        if (error !== 'cancel') {
          console.error('Error activating device:', error)
        }
      }
    },

    async deactivateDevice(device) {
      try {
        await this.$confirm('Are you sure you want to deactivate this device?', 'Confirm Action', {
          type: 'warning'
        })
        
        const deviceData = {
          action: 'DEACTIVATE',
          username: device.username,
          fcmToken: device.deviceToken || device.fcmToken || device.idDevice // Support multiple field names
        }

        await this.$store.dispatch('device/manageDevice', deviceData)
        await this.loadAllDevices(this.currentPage - 1) // Maintain current page
        
      } catch (error) {
        if (error !== 'cancel') {
          console.error('Error deactivating device:', error)
        }
      }
    },

    async removeDevice(device) {
      try {
        await this.$confirm('Are you sure you want to remove this device? This action cannot be undone.', 'Confirm Removal', {
          type: 'warning'
        })
        
        const deviceData = {
          action: 'REMOVE',
          username: device.username,
          fcmToken: device.deviceToken || device.fcmToken || device.idDevice // Support multiple field names
        }

        await this.$store.dispatch('device/manageDevice', deviceData)
        await this.loadAllDevices(this.currentPage - 1) // Maintain current page
        
      } catch (error) {
        if (error !== 'cancel') {
          console.error('Error removing device:', error)
        }
      }
    },

    getDeviceIcon(deviceType) {
      switch (deviceType?.toLowerCase()) {
        case 'android':
          return 'android'
        case 'ios':
          return 'apple'
        case 'web':
          return 'globe'
        default:
          return 'mobile-alt'
      }
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A'
      // Add 7 hours to the date
      return moment(dateString).add(7, 'hours').format('DD/MM/YYYY HH:mm')
    }
  }
}
</script>

<style scoped>
.all-devices-management {
  padding: 20px;
}

.filter-form .el-form-item {
  margin-bottom: 10px;
}

.device-detail h4 {
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 8px;
}
</style>
